import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  artistList,
  artistDetail,
  createArtist,
  updateArtist,
  deleteArtist,
  generateBulkInviteLinks,
  IBulkInviteResult,
} from "./callbacks";
import { QData } from "../../../types/basic-type-list";
import { IArtist } from "../../../types/api/artist";
import { TResponseWithQData } from "../../../types/common";

export const ARTIST_KEYS = {
  all: ["artists"] as const,
  lists: () => [...ARTIST_KEYS.all, "list"] as const,
  list: (qData: QData) => [...ARTIST_KEYS.lists(), qData] as const,
  details: () => [...ARTIST_KEYS.all, "detail"] as const,
  detail: (id: string) => [...ARTIST_KEYS.details(), id] as const,
};

export function useArtistList(
  qData: QData & { search?: string; status?: string },
  options?: any,
) {
  const [extraQuery, setExtraQuery] = useState<Record<string, any>>({});

  const mergedQData = React.useMemo(() => {
    return { ...qData, ...extraQuery };
  }, [qData, extraQuery]);

  const query = useQuery<TResponseWithQData<IArtist[]>>({
    queryKey: ARTIST_KEYS.list(mergedQData),
    queryFn: async () => {
      const { data } = await artistList(mergedQData);
      return data;
    },
    ...options,
  });

  const refetchWithQuery = React.useCallback((newQData: typeof qData) => {
    setExtraQuery(newQData);
  }, []);

  return { ...query, refetchWithQuery };
}

export function useArtistDetail(id: string, options?: any) {
  return useQuery({
    queryKey: ARTIST_KEYS.detail(id),
    queryFn: async () => {
      const { data } = await artistDetail(id);
      return data;
    },
    enabled: !!id,
    ...options,
  });
}

export function useArtistCreate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData & { search?: string; status?: string };
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createArtist,
    onSuccess: (res) => {
      if (qData) {
        queryClient.setQueryData(
          ARTIST_KEYS.list(qData),
          (old: TResponseWithQData<IArtist[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: [res.data.data, ...(old.data || [])],
              extra: {
                ...old.extra,
                total: (old.extra?.total || 0) + 1,
              },
            };
          },
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ARTIST_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
}

export function useArtistUpdate({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData & { search?: string; status?: string };
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateArtist,
    onSuccess: (res) => {
      const updatedItem = res.data.data;

      if (qData) {
        queryClient.setQueryData(
          ARTIST_KEYS.list(qData),
          (old: TResponseWithQData<IArtist[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.map((item) =>
                item._id === updatedItem._id ? updatedItem : item,
              ),
            };
          },
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ARTIST_KEYS.lists() });
      }

      queryClient.setQueryData(
        ARTIST_KEYS.detail(updatedItem._id),
        (old: any) => {
          if (!old) return old;
          return { ...old, data: updatedItem };
        },
      );

      onSuccess?.();
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
}

export function useBulkInviteLinks({
  onSuccess,
  onError,
}: {
  onSuccess?: (result: IBulkInviteResult, message: string) => void;
  onError?: (err: any) => void;
} = {}) {
  return useMutation({
    mutationFn: generateBulkInviteLinks,
    onSuccess: (res) => {
      onSuccess?.(
        res.data.data || { invites: [], sent: 0, failed: [] },
        res.data.message || "",
      );
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
}

export function useArtistDelete({
  qData,
  onSuccess,
  onError,
}: {
  qData?: QData & { search?: string; status?: string };
  onSuccess?: () => void;
  onError?: (err: any) => void;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteArtist,
    onSuccess: (_, deletedId) => {
      if (qData) {
        queryClient.setQueryData(
          ARTIST_KEYS.list(qData),
          (old: TResponseWithQData<IArtist[]> | undefined) => {
            if (!old) return old;
            return {
              ...old,
              data: old.data?.filter((item) => item._id !== deletedId),
              extra: {
                ...old.extra,
                total: Math.max(0, (old.extra?.total || 1) - 1),
              },
            };
          },
        );
      } else {
        queryClient.invalidateQueries({ queryKey: ARTIST_KEYS.lists() });
      }
      onSuccess?.();
    },
    onError: (err: any) => {
      onError?.(err);
    },
  });
}
